/**
 * EditAutocompleteCell - Reusable multi-select autocomplete for DataGrid inline editing
 *
 * Handles users, contacts, organizations, and any entity with a UUID.
 * Updates are triggered when the dropdown closes (not on every selection).
 *
 * Usage in column definition:
 * ```tsx
 * {
 *   field: 'team.AM',
 *   editable: true,
 *   renderEditCell: (params) => (
 *     <EditAutocompleteCell
 *       params={params}
 *       options={users}
 *       getOptionLabel={(user) => user.name}
 *       getOptionValue={(user) => user.user_uuid}
 *       multiple
 *     />
 *   ),
 * }
 * ```
 */

import { Avatar, AvatarGroup, Autocomplete, Box, Chip, TextField, useTheme } from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import { useCallback, useEffect, useRef, useState } from 'react'

interface EditAutocompleteCellProps<T = any> {
  params: GridRenderEditCellParams
  options: T[]
  getOptionLabel: (option: T) => string
  getOptionValue: (option: T) => string
  /** Whether to allow multiple selections */
  multiple?: boolean
  /** Optional: render avatar for each option */
  getOptionImage?: (option: T) => string | undefined
  /** Placeholder text */
  placeholder?: string
  /** Field used to compare options (defaults to getOptionValue result) */
  isOptionEqualToValue?: (option: T, value: T) => boolean
}

export const EditAutocompleteCell = <T extends Record<string, any>>({
  params,
  options,
  getOptionLabel,
  getOptionValue,
  multiple = true,
  getOptionImage,
  placeholder = 'Search...',
  isOptionEqualToValue,
}: EditAutocompleteCellProps<T>) => {
  const apiRef = useGridApiContext()
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const hasCommittedRef = useRef(false)
  const [open, setOpen] = useState(true) // Keep dropdown open initially

  // Parse current value - could be array of UUIDs or array of objects
  const parseCurrentValue = (): T[] => {
    const value = params.value
    if (!value) return []
    if (!Array.isArray(value)) return []

    return value
      .map((item: any) => {
        // If it's already an object with the expected structure, find it in options
        if (typeof item === 'object' && item !== null) {
          const uuid = getOptionValue(item)
          return options.find(opt => getOptionValue(opt) === uuid) || item
        }
        // If it's a string UUID, find the matching option
        if (typeof item === 'string') {
          return options.find(opt => getOptionValue(opt) === item)
        }
        return null
      })
      .filter(Boolean) as T[]
  }

  const [selectedValues, setSelectedValues] = useState<T[]>(parseCurrentValue)

  // Focus input when cell enters edit mode
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const handleChange = useCallback(
    (_event: any, newValue: T | T[] | null) => {
      if (multiple) {
        setSelectedValues(newValue as T[])
      } else {
        setSelectedValues(newValue ? [newValue as T] : [])
      }
    },
    [multiple]
  )

  // Commit the value when dropdown closes
  const handleClose = useCallback(() => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    setOpen(false)

    // Extract UUIDs from selected objects
    const newValue = selectedValues.map(item => getOptionValue(item))

    // Update the cell value
    apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value: multiple ? newValue : newValue[0] || null,
    })

    // Exit edit mode
    apiRef.current.stopCellEditMode({ id: params.id, field: params.field })
  }, [apiRef, params.id, params.field, selectedValues, getOptionValue, multiple])

  // Handle escape key to cancel without saving
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        hasCommittedRef.current = true
        setOpen(false)
        apiRef.current.stopCellEditMode({ id: params.id, field: params.field, ignoreModifications: true })
      }
    },
    [apiRef, params.id, params.field]
  )

  const defaultIsOptionEqualToValue = (option: T, value: T) => getOptionValue(option) === getOptionValue(value)

  return (
    <Autocomplete
      fullWidth
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      multiple={multiple}
      options={options}
      value={multiple ? selectedValues : selectedValues[0] || null}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
      disableCloseOnSelect={multiple}
      filterOptions={(opts, { inputValue }) => {
        const search = inputValue.toLowerCase()
        return opts.filter(opt => getOptionLabel(opt).toLowerCase().includes(search))
      }}
      sx={{
        bgcolor: 'transparent', // Transparent so cell highlight borders show through
        '& .MuiAutocomplete-inputRoot': {
          bgcolor: 'transparent',
          flexWrap: 'nowrap',
        },
      }}
      renderInput={inputParams => (
        <TextField
          {...inputParams}
          inputRef={inputRef}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'transparent',
              py: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none', // Remove border so it doesn't compete with cell highlight
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        getOptionImage ? (
          <AvatarGroup max={3} spacing="small" sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
            {tagValue.map(option => (
              <Avatar key={getOptionValue(option)} alt={getOptionLabel(option)} src={getOptionImage(option)} />
            ))}
          </AvatarGroup>
        ) : (
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index })
            return <Chip key={key} {...chipProps} label={getOptionLabel(option)} size="small" />
          })
        )
      }
      renderOption={(props, option) => {
        const { key, ...otherProps } = props
        return (
          <Box
            component="li"
            key={getOptionValue(option)}
            {...otherProps}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {getOptionImage && (
              <Avatar src={getOptionImage(option)} alt={getOptionLabel(option)} sx={{ width: 24, height: 24 }} />
            )}
            {getOptionLabel(option)}
          </Box>
        )
      }}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: { zIndex: 1400 },
        },
        paper: {
          sx: {
            bgcolor: theme.palette.background.paper,
            minWidth: 220,
            maxHeight: 250,
          },
        },
      }}
    />
  )
}

export default EditAutocompleteCell
